import socket from './socket';

// Channel socket rooms, joined lazily (.specs/messages.md, "Socket rooms").
//
// The server joins a socket to its DMs and groups at connect. A channel's
// room it joins only when asked: every space and factiii has a channel, and a
// socket in all of them is a room per channel for nothing. The conversation
// list does not need the room (the server sends what the list shows to the
// user's own room); the open transcript does - reactions, edits, thread
// replies and reply counts travel only through it.
//
// A room stays joined for the life of the page or app session once its
// channel was opened, so a transcript that was closed and opened again is
// still current. After a reconnect the server has forgotten the socket's
// rooms, and every one in `opened` is asked for again.

/** How long one join waits for its answer before it is sent again. */
const JOIN_TIMEOUT_MS = 5000;

/** The channels this client has opened. */
const opened = new Set<string>();
/** The joins that the server has answered on the current connection. */
const joined = new Set<string>();

/** Ask for the room. Resolves when the server has answered, or when there
 *  is no connection to ask on. */
const join = (conversationId: string): Promise<void> =>
  new Promise((resolve) => {
    if (!socket.connected) {
      // Not connected: `connect` below asks for it.
      resolve();
      return;
    }
    socket
      .timeout(JOIN_TIMEOUT_MS)
      .emit('joinConversation', conversationId, (error, allowed) => {
        if (error) {
          // No answer. The socket may have dropped (then `connect` asks
          // again) or the server was slow: ask again while it is still wanted.
          if (opened.has(conversationId) && socket.connected) {
            void join(conversationId).then(resolve);
          } else {
            resolve();
          }
          return;
        }
        if (allowed) {
          joined.add(conversationId);
        } else {
          // Refused: this user may not read it (removed, or another account
          // signed in on this client). Stop asking.
          opened.delete(conversationId);
        }
        resolve();
      });
  });

socket.on('connect', () => {
  joined.clear();
  for (const conversationId of opened) void join(conversationId);
});

/**
 * Join the room of a channel the user has just opened. Resolves once the
 * server has answered, with true when the socket was not in the room before
 * this call: events from before it were not delivered, so the caller loads
 * the transcript fresh. A DM or a group resolves false at once - the server
 * joined it at connect.
 */
export const openConversationRoom = (conversation: {
  id: string;
  kind: 'DM' | 'GROUP' | 'CHANNEL';
}): Promise<boolean> => {
  if (conversation.kind !== 'CHANNEL') return Promise.resolve(false);
  if (joined.has(conversation.id)) return Promise.resolve(false);
  opened.add(conversation.id);
  return join(conversation.id).then(() => true);
};

/** The user is no longer in this conversation: do not rejoin its room. */
export const forgetConversationRoom = (conversationId: string): void => {
  opened.delete(conversationId);
  joined.delete(conversationId);
};
